import { clamp } from '../util.js';
import { sigmoid } from '../v2/logisticRegression.js';
const LOGIT_EPS = 1e-6;
function logit(p) {
    const c = clamp(p, LOGIT_EPS, 1 - LOGIT_EPS);
    return Math.log(c / (1 - c));
}
export const DEFAULT_V4_FIT_OPTIONS = {
    weightGridStep: 0.05,
    temperatureGrid: [0.5, 0.75, 1, 1.25, 1.5, 2, 3],
    interceptGrid: [-0.5, -0.3, -0.15, -0.05, 0, 0.05, 0.15, 0.3, 0.5],
    calibrationFraction: 0.3,
    shrinkageFullConfidenceRows: 250,
    shrinkageFloor: 0.3,
};
/**
 * V4 will not fit at all below this many common, chronologically ordered
 * out-of-fold rows — `fitV4Ensemble` returns `ok: false` instead, never an
 * invented probability. This is the plan's own "do not fit with fewer than
 * 160 samples" minimum; treat 320+ as the bar for actually trusting/
 * promoting a fit, not just accepting one (that promotion decision belongs
 * to whatever wires this live, not to this pure fitting function).
 */
export const MIN_OOF_ROWS_TO_FIT = 160;
function blendedPUp(weights, row) {
    return weights.v1 * row.pUpV1 + weights.v2 * row.pUpV2 + weights.v3 * row.pUpV3;
}
function meanLogLoss(rows, pUpFor) {
    let sum = 0;
    for (const row of rows) {
        const p = clamp(pUpFor(row), LOGIT_EPS, 1 - LOGIT_EPS);
        sum -= row.actualUp ? Math.log(p) : Math.log(1 - p);
    }
    return sum / rows.length;
}
/**
 * Deterministic grid search over the weight simplex (w1+w2+w3=1, all >= 0)
 * minimizing mean log-loss on `rows` — a grid rather than a gradient-based
 * optimizer, per the plan's own preference to avoid an optimizer
 * dependency, and small enough (≤ ~230 candidates at the default step) to
 * run comfortably inside a serverless function.
 */
function fitConvexWeights(rows, step) {
    const steps = Math.max(1, Math.round(1 / step));
    let best = { v1: 1, v2: 0, v3: 0 };
    let bestLoss = Infinity;
    for (let i = 0; i <= steps; i++) {
        const w1 = i / steps;
        for (let j = 0; j <= steps - i; j++) {
            const w2 = j / steps;
            const w3 = 1 - w1 - w2;
            const weights = { v1: w1, v2: w2, v3: Math.max(0, w3) };
            const loss = meanLogLoss(rows, (row) => blendedPUp(weights, row));
            if (loss < bestLoss) {
                bestLoss = loss;
                best = weights;
            }
        }
    }
    return best;
}
/** Deterministic grid search for temperature + intercept recalibration, fit on a later slice the weights themselves never saw. */
function fitCalibration(rows, weights, temperatureGrid, interceptGrid) {
    let best = { temperature: 1, interceptLogit: 0 };
    let bestLoss = Infinity;
    for (const temperature of temperatureGrid) {
        for (const interceptLogit of interceptGrid) {
            const loss = meanLogLoss(rows, (row) => {
                const z = logit(blendedPUp(weights, row)) / temperature + interceptLogit;
                return sigmoid(z);
            });
            if (loss < bestLoss) {
                bestLoss = loss;
                best = { temperature, interceptLogit };
            }
        }
    }
    return best;
}
/**
 * Fits V4's ensemble weights on the earlier portion of `rows` and its
 * temperature/intercept recalibration on a strictly later slice the
 * weights were never fit on — training window, then a later calibration
 * window, the same discipline this package's other fit procedures follow.
 * `rows` need not be pre-sorted; this sorts by `roundStartMs` itself.
 */
export function fitV4Ensemble(rows, options = {}) {
    const opts = { ...DEFAULT_V4_FIT_OPTIONS, ...options };
    if (rows.length < MIN_OOF_ROWS_TO_FIT) {
        return {
            ok: false,
            model: null,
            reason: `only ${rows.length} common out-of-fold rows; need at least ${MIN_OOF_ROWS_TO_FIT}.`,
        };
    }
    const ordered = [...rows].sort((a, b) => a.roundStartMs - b.roundStartMs);
    const calibrationCount = Math.max(1, Math.round(ordered.length * opts.calibrationFraction));
    const trainRows = ordered.slice(0, ordered.length - calibrationCount);
    const calibrationRows = ordered.slice(ordered.length - calibrationCount);
    const weights = fitConvexWeights(trainRows, opts.weightGridStep);
    const { temperature, interceptLogit } = fitCalibration(calibrationRows, weights, opts.temperatureGrid, opts.interceptGrid);
    const probabilityShrinkage = clamp(calibrationRows.length / opts.shrinkageFullConfidenceRows, opts.shrinkageFloor, 1);
    return {
        ok: true,
        reason: null,
        model: {
            schemaVersion: 1,
            modelVersion: 'v4',
            trainedAtMs: Date.now(),
            weights,
            interceptLogit,
            temperature,
            probabilityShrinkage,
            trainingRows: trainRows.length,
            calibrationRows: calibrationRows.length,
            trainingStartRoundMs: ordered[0].roundStartMs,
            trainingEndRoundMs: ordered[ordered.length - 1].roundStartMs,
        },
    };
}
/**
 * Predicts P(UP) from the three base models' own P(UP) values — never
 * their `{action, probability}` form, since a DOWN call's `probability`
 * means the opposite thing (see oof.ts). Clamped to [0.01, 0.99] only for
 * numerical safety, never to force confidence away from 0.5.
 */
export function predictV4Probability(model, inputs) {
    const pBlend = clamp(model.weights.v1 * inputs.pUpV1 +
        model.weights.v2 * inputs.pUpV2 +
        model.weights.v3 * inputs.pUpV3, 0, 1);
    const z = logit(pBlend) / model.temperature + model.interceptLogit;
    const pCalibrated = sigmoid(z);
    const pShrunk = 0.5 + model.probabilityShrinkage * (pCalibrated - 0.5);
    return clamp(pShrunk, 0.01, 0.99);
}
/** Converts a raw P(UP) into this package's shared `{action, probability}` contract (probability always >= 0.5, for whichever action was picked). */
export function toDirectionalPrediction(pUp) {
    const bounded = clamp(pUp, 0.01, 0.99);
    return bounded >= 0.5
        ? { action: 'UP', probability: bounded }
        : { action: 'DOWN', probability: 1 - bounded };
}
//# sourceMappingURL=ensemble.js.map